import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <img src="/kyle-drive-logo.png" alt="Kyle Drive" className="h-20 w-auto" />
            <div>
              <h1 className="text-4xl font-bold text-[hsl(210,100%,20%)]">Privacy Policy</h1>
              <p className="text-gray-600 mt-2">Last updated: December 2025</p>
            </div>
          </div>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                Kyle Drive ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our driving experience services and website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">2. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Personal Information</h3>
                  <ul className="list-disc list-inside space-y-2 pl-4">
                    <li>Name, date of birth, and contact details (email, phone number)</li>
                    <li>Parent/guardian contact information for participants under 18</li>
                    <li>Emergency contact details</li>
                    <li>Payment and billing information</li>
                    <li>Medical information relevant to safe participation (if voluntarily provided)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Usage Information</h3>
                  <ul className="list-disc list-inside space-y-2 pl-4">
                    <li>Booking history and session attendance records</li>
                    <li>Website usage data and analytics</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-3">We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Process and manage your bookings and payments</li>
                <li>Communicate with you about your sessions, changes, or cancellations</li>
                <li>Ensure the safety and wellbeing of all participants</li>
                <li>Improve our services and customer experience</li>
                <li>Comply with legal obligations and safety requirements</li>
                <li>Send you marketing communications (with your consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">4. Data Sharing and Disclosure</h2>
              <p className="leading-relaxed mb-3">We may share your information with:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Service Providers:</strong> Payment processors, booking system providers</li>
                <li><strong>Instructors:</strong> Essential information for delivering your sessions safely</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
                <li><strong>Emergency Services:</strong> In case of medical or safety emergencies</li>
              </ul>
              <p className="leading-relaxed mt-3">
                We do not sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">5. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, loss, or misuse. Payment information is processed securely through encrypted payment gateways.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">6. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. Typically, this means:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4 mt-3">
                <li>Booking records: 7 years (for tax and insurance purposes)</li>
                <li>Marketing consent: Until you withdraw consent</li>
                <li>Website analytics: Anonymized after 26 months</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">7. Your Rights</h2>
              <p className="leading-relaxed mb-3">Under UK GDPR, you have the right to:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate or incomplete data</li>
                <li>Request deletion of your data (subject to legal requirements)</li>
                <li>Object to processing of your data</li>
                <li>Request restriction of processing</li>
                <li>Data portability</li>
                <li>Withdraw consent for marketing communications at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">8. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our services are designed for young people aged 10 and above. For participants under 18, we require parental/guardian consent and contact information. We take extra care to protect the privacy of minors and only collect information necessary for safe service delivery.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">9. Cookies and Tracking</h2>
              <p className="leading-relaxed">
                Our website uses essential cookies for functionality and analytics cookies to improve user experience. You can control cookie preferences through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">10. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on our website and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[hsl(210,100%,20%)] mb-4">11. Contact Us</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
              </p>
              <div className="bg-cyan-50 rounded-xl p-6 border-2 border-cyan-200">
                <p className="font-semibold text-gray-900">Kyle Drive</p>
                <p className="text-gray-700">Kyle Rise Car Park, Carrick Street, Ayr, KA7 1RZ</p>
                <p className="text-gray-700 mt-2">
                  <strong>Email:</strong> <a href="mailto:wheels@kyledrive.co.uk" className="text-cyan-600 hover:text-cyan-700">wheels@kyledrive.co.uk</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
